// useProgramme.ts — loads the ordered programme from the content provider.
// Models loading and error states so the UI can show LoadingState / ErrorState,
// which matters once the source is a network-backed CMS.

import React from 'react';
import type { Programme } from '@/types';
import { contentProvider } from '@/services/contentProvider';

interface ProgrammeState {
  programme: Programme | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useProgramme(): ProgrammeState {
  const [programme, setProgramme] = React.useState<Programme | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    contentProvider
      .getProgramme()
      .then((p) => {
        if (active) {
          setProgramme(p);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (active) {
          setError(e instanceof Error ? e : new Error('Could not load the programme.'));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  return { programme, loading, error, reload };
}
